import type { JsonApiResponse } from '~/interfaces/api/main';
import type {
  CreateCoBorrowerPayload,
  IdentityChild,
  IdentityPerson,
  PersonReferentials,
  ReplaceChildrenPayload,
  UpdatePersonPayload,
} from '~/interfaces/opportunities/identity/main';
import { useAuthFetchNext } from '~/composables/useAuthFetch.next';
import { authMutate } from '~/composables/http/authMutate';
import { isAPIErrorResponse } from '~/composables/useUtils';
import { createApiError, unwrapApiItem } from '~/api/utils';

export const getOpportunityPersons = async (
  opportunityHashId: string,
): Promise<IdentityPerson[]> => {
  const response = await useAuthFetchNext(`opportunities/${opportunityHashId}/persons`);

  if (isAPIErrorResponse(response)) {
    throw createApiError('Les personnes du dossier n\'ont pas pu être récupérées', response.errors);
  }

  const payload = response as JsonApiResponse<IdentityPerson[]>;
  return payload.data ?? [];
};

export const getOpportunityChildren = async (
  opportunityHashId: string,
): Promise<IdentityChild[]> => {
  const response = await useAuthFetchNext(`opportunities/${opportunityHashId}/children`);

  if (isAPIErrorResponse(response)) {
    throw createApiError('Les enfants du dossier n\'ont pas pu être récupérés', response.errors);
  }

  const payload = response as JsonApiResponse<IdentityChild[]>;
  return payload.data ?? [];
};

export const getPersonReferentials = async (): Promise<PersonReferentials | null> => {
  const response = await useAuthFetchNext('referentials/persons');

  if (isAPIErrorResponse(response)) {
    throw createApiError('Les référentiels de personne n\'ont pas pu être récupérés', response.errors);
  }

  const payload = response as JsonApiResponse<PersonReferentials>;
  return payload.data ?? null;
};

export const updateOpportunityPerson = async (
  opportunityHashId: string,
  personId: number,
  payload: UpdatePersonPayload,
): Promise<IdentityPerson | null> => {
  const response = await authMutate(`opportunities/${opportunityHashId}/persons/${personId}`, {
    method: 'PATCH',
    body: payload,
    silentError: true,
  });

  if (isAPIErrorResponse(response)) {
    throw createApiError('La personne n\'a pas pu être mise à jour', response.errors);
  }

  return unwrapApiItem<IdentityPerson>(response);
};

export const replaceOpportunityChildren = async (
  opportunityHashId: string,
  payload: ReplaceChildrenPayload,
): Promise<IdentityChild[]> => {
  const response = await authMutate(`opportunities/${opportunityHashId}/children`, {
    method: 'PUT',
    body: payload,
    silentError: true,
  });

  if (isAPIErrorResponse(response)) {
    throw createApiError('Les enfants du dossier n\'ont pas pu être remplacés', response.errors);
  }

  const data = response as JsonApiResponse<IdentityChild[]>;
  return data.data ?? [];
};

export const createOpportunityCoBorrower = async (
  opportunityHashId: string,
  payload: CreateCoBorrowerPayload,
): Promise<void> => {
  const response = await authMutate(`opportunities/${opportunityHashId}/persons/co-borrower`, {
    method: 'POST',
    body: payload,
    silentError: true,
  });

  if (isAPIErrorResponse(response)) {
    throw createApiError('Le co-emprunteur n\'a pas pu être créé', response.errors);
  }
};

export const deleteOpportunityCoBorrower = async (
  opportunityHashId: string,
): Promise<void> => {
  const response = await authMutate(`opportunities/${opportunityHashId}/persons/co-borrower`, {
    method: 'DELETE',
    silentError: true,
  });

  if (isAPIErrorResponse(response)) {
    throw createApiError('Le co-emprunteur n\'a pas pu être supprimé', response.errors);
  }
};

export const swapOpportunityBorrowers = async (
  opportunityHashId: string,
): Promise<void> => {
  const response = await authMutate(`opportunities/${opportunityHashId}/persons/swap`, {
    method: 'POST',
    silentError: true,
  });

  if (isAPIErrorResponse(response)) {
    throw createApiError('Les rôles des deux emprunteurs n\'ont pas pu être intervertis', response.errors);
  }
};
